import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { LineChart } from '@/components/icons';
import { useAuth } from '@/auth/AuthProvider';
import { apiFetch } from '@/lib/api-fetch';
import { MAX_HITS_PER_GROUP, type CommandEntityHit } from './types';

/**
 * Evaluation entity-search adapter (W129-F5-S2 / 3rd of 3).
 *
 * Senviro doesn't have a generic `GET /api/v1/evaluations` list endpoint —
 * each evaluation is per-building or per-template-version. But the W129-F2
 * smart-inbox endpoint already returns the principal's 5 most-recently
 * touched evaluations under `recentEvaluations`. We reuse that response
 * via the same TanStack Query key (`['dashboard', 'smart-inbox']`) so the
 * `<SmartInboxWidget>` and this adapter share a single network call.
 *
 * The recent-list is small (max 5 rows already capped by the BE) so the
 * substring filter runs over a tiny set. `framework` is the searchable
 * field (BREEAM, CSRD, GRESB, …) since evaluations don't have a friendly
 * `name`.
 */
interface SmartInboxEvaluation {
  readonly evaluationId: string;
  readonly framework: string;
  readonly updatedAt: string;
  readonly drillUrl: string;
}

interface SmartInboxResponse {
  readonly recentEvaluations: ReadonlyArray<SmartInboxEvaluation>;
  // Other fields exist (topDeadlines, recentIncidents) but we don't need
  // them here — TanStack Query returns the full payload either way.
}

export function useEvaluationsCommandHits(query: string): readonly CommandEntityHit[] {
  const { getAccessToken, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Same key + fetcher as <SmartInboxWidget> — TanStack dedupes the request
  // so opening the palette doesn't fire a second network call when the
  // executive dashboard already mounted the widget.
  const inboxQuery = useQuery({
    queryKey: ['dashboard', 'smart-inbox'],
    queryFn: () =>
      apiFetch<SmartInboxResponse>('/api/v1/dashboard/smart-inbox', {
        getAccessToken,
      }),
    enabled: isAuthenticated,
    staleTime: 60_000,
  });

  return useMemo<readonly CommandEntityHit[]>(() => {
    const trimmed = query.trim();
    if (!trimmed) return [];
    const evaluations = inboxQuery.data?.recentEvaluations ?? [];
    if (evaluations.length === 0) return [];

    const needle = trimmed.toLowerCase();
    const matched = evaluations.filter((e) =>
      e.framework.toLowerCase().includes(needle),
    );
    return matched.slice(0, MAX_HITS_PER_GROUP).map<CommandEntityHit>((e) => ({
      id: `evaluation:${e.evaluationId}`,
      label: e.framework,
      sublabel: relativeUpdatedLabel(e.updatedAt),
      kind: 'evaluations',
      icon: LineChart,
      onSelect: () => navigate(e.drillUrl),
    }));
  }, [query, inboxQuery.data, navigate]);
}

/** Compact "12d ago" / "today" label for the recent-evaluation sublabel. */
function relativeUpdatedLabel(iso: string): string {
  const now = Date.now();
  const at = new Date(iso).getTime();
  const diffDays = Math.round((at - now) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'today';
  if (diffDays === -1) return 'yesterday';
  if (diffDays < -1 && diffDays > -30) return `${Math.abs(diffDays)}d ago`;
  if (diffDays > 0) return new Date(iso).toLocaleDateString();
  return new Date(iso).toLocaleDateString();
}
